import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { NewsletterTextarea } from './NewsletterTextarea';
import { Mail, Send, Sparkles, Shield, CheckCircle } from 'lucide-react';
import { EditableEmail } from './EditableEmail';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { detectDeviceInfo } from '@/utils/deviceDetection';
import { trackGTMEvent, trackGTMConversion, trackGTMSubscription, trackGTMConversionFunnel, trackGTMFormEngagement, trackGTMBeginCheckout, trackGTMPurchase } from './GoogleTagManager';
import { trackConversionFunnelHj, trackFormEngagementHj } from './Hotjar';
import { trackSubscriptionMp, trackEventMp, trackConversionFunnelMp, trackFormEngagementMp } from './Mixpanel';
import { sentryUtils } from '@/utils/sentry';
import ErrorBoundary from './ErrorBoundary';
import { useTranslation } from 'react-i18next';
// useFeatureFlag removed - inline checkout is now always enabled
import { PlanSelector } from './PlanSelector';
import { InlineCheckoutModal } from './InlineCheckoutModal';
import { useConditionalPricing } from '@/hooks/useConditionalPricing';

const createSchemas = (t: any) => {
  const emailSchema = z.object({
    email: z
      .string()
      .min(1, t('newsletterForm.validation.emailRequired'))
      .email(t('newsletterForm.validation.emailInvalid'))
      .refine((email) => !email.includes('+'), {
        message: t('newsletterForm.validation.emailInvalid')
      })
  });

  const interestsSchema = z.object({
    interests: z
      .string()
      .min(30, t('newsletterForm.validation.interestsMin'))
      .max(500, t('newsletterForm.validation.interestsMax')),
  });

  return { emailSchema, interestsSchema };
};

type EmailData = {
  email: string;
};
type InterestsData = {
  interests: string;
};

interface NewsletterFormProps {
  onSuccess?: (email: string) => void;
  onDecommissioningOpen: () => void;
}

export const NewsletterForm = ({ onSuccess, onDecommissioningOpen }: NewsletterFormProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { emailSchema, interestsSchema } = createSchemas(t);
  const [step, setStep] = useState(1);
  const [userEmail, setUserEmail] = useState('');
  const [showModal, setShowModal] = useState(false);
  
  // Inline checkout states - always enabled (100% rollout)
  const inlineCheckoutEnabled = true;
  const [showPlanSelector, setShowPlanSelector] = useState(false);
  const [showInlineCheckout, setShowInlineCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const pricingData = useConditionalPricing(true);
  
  // Step 1 form - Email only
  const emailForm = useForm<EmailData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: ''
    }
  });

  // Step 2 form - Interests only
  const interestsForm = useForm<InterestsData>({
    resolver: zodResolver(interestsSchema),
    defaultValues: {
      interests: ''
    }
  });

  // Auto-resume flow from remarketing link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cont = params.get('continue');
    const email = params.get('email');
    if (cont && email) {
      try {
        emailForm.setValue('email', email);
        setUserEmail(email);
        setShowModal(true);
        trackGTMEvent('prospect_reengaged', { user_email: email });
      } catch (e) {
        console.error('Failed to auto-resume from URL params', e);
      }
    }
  }, []);
  const handleEmailSubmit = async (data: EmailData) => {
    try {
      // Track funnel step - email entry with GTM
      trackGTMBeginCheckout(data.email);
      trackGTMConversionFunnel('email_entered', 2, 2);
      trackGTMFormEngagement('email', 'submit', 'email');
      trackConversionFunnelHj('email_entered', 2);
      trackConversionFunnelMp('email_entered', 2);
      trackFormEngagementHj('email', 'submit');
      trackFormEngagementMp('email', 'submit');
      sentryUtils.trackNewsletterSubmission(data.email, 'email');
      sentryUtils.trackConversionFunnel('email_entered', 2, { email: data.email });

      const email = data.email.trim();
      const deviceInfo = detectDeviceInfo();

      const params = new URLSearchParams(window.location.search);
      const payload = {
        email,
        browserName: deviceInfo.browserName,
        browserVersion: deviceInfo.browserVersion,
        deviceType: deviceInfo.deviceType,
        operatingSystem: deviceInfo.operatingSystem,
        userAgent: deviceInfo.userAgent,
        deviceLanguage: navigator.language,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined,
        referrer_url: document.referrer || undefined,
        landing_page_url: window.location.href,
        checkout_flow_version: 'decommissioning',
        capture_context: 'decommissioning',
      };

      const { data: prospectData, error } = await sentryUtils.trackSupabaseFunction(
        'capture-prospect',
        () => supabase.functions.invoke('capture-prospect', { body: payload }),
        { email: data.email, deviceType: deviceInfo.deviceType }
      );

      if (error || prospectData?.error) {
        const errorType = prospectData?.error;
        
        // Handle specific validation errors
        if (errorType === 'ALREADY_SUBSCRIBED') {
          toast({
            title: t('emailValidation.alreadySubscribed'),
            description: t('emailValidation.alreadySubscribedDescription'),
          });
          return;
        }
        
        if (errorType === 'TYPO_DETECTED') {
          toast({
            title: t('emailValidation.typoDetected'),
            description: t('emailValidation.typoSuggestion', { suggestion: prospectData.suggestion }),
          });
          return;
        }
        
        if (errorType === 'DISPOSABLE_DOMAIN') {
          toast({
            title: t('emailValidation.disposableDomain'),
            description: t('emailValidation.disposableDescription'),
            variant: 'destructive',
          });
          return;
        }
        
        if (errorType === 'INVALID_FORMAT') {
          toast({
            title: t('emailValidation.invalidFormat'),
            description: t('emailValidation.invalidDescription'),
            variant: 'destructive',
          });
          return;
        }

        // Generic error handling
        sentryUtils.captureError(new Error('Prospect capture failed'), {
          email: data.email,
          error: error?.message,
          deviceInfo
        });
        toast({
          title: t('newsletterForm.toasts.emailSaveError.title'),
          description: t('newsletterForm.toasts.emailSaveError.description'),
          variant: 'destructive',
        });
        return;
      }

      // Track prospect captured
      trackGTMEvent('prospect_captured', { 
        user_email: email, 
        prospect_id: prospectData?.prospect_id 
      });
      trackEventMp('prospect_captured', { 
        user_email: email,
        prospect_id: prospectData?.prospect_id 
      });

      trackGTMEvent('decommissioning_lead_captured', { source: 'newsletter_form' });
      trackEventMp('decommissioning_lead_captured', { source: 'newsletter_form' });
      onDecommissioningOpen();
    } catch (e) {
      console.error('Unexpected error on email submit:', e);
      toast({
        title: t('newsletterForm.toasts.unexpectedError.title'),
        description: t('newsletterForm.toasts.unexpectedError.description'),
        variant: 'destructive',
      });
    }
  };

  const handleFinalSubmit = async (data: InterestsData) => {
    // ⚠️ This function is ONLY called in LEGACY flow
    // Inline flow redirects to /interests page and never opens this modal
    
    try {
      // Track funnel completion with GTM
      trackGTMPurchase(userEmail, 'trial');
      trackGTMConversionFunnel('interests_submitted', 4, 10);
      trackGTMFormEngagement('interests', 'submit', 'interests');
      trackGTMSubscription(userEmail, 'trial');
      trackGTMConversion('newsletter_signup', 10, 'newsletter_conversion');
      trackConversionFunnelHj('interests_submitted', 4);
      trackConversionFunnelMp('interests_submitted', 4);
      trackFormEngagementHj('interests', 'submit');
      trackFormEngagementMp('interests', 'submit');
      sentryUtils.trackNewsletterSubmission(userEmail, 'interests');
      sentryUtils.trackConversionFunnel('interests_submitted', 4, { email: userEmail });

      // Detect device and browser information
      const deviceInfo = detectDeviceInfo();
      
      // Capture UTM parameters from URL
      const params = new URLSearchParams(window.location.search);
      
      // Prepare subscription data
      const subscriptionData = {
        interests: data.interests.trim(),
        email: userEmail.trim(),
        browserName: deviceInfo.browserName,
        browserVersion: deviceInfo.browserVersion,
        deviceType: deviceInfo.deviceType,
        operatingSystem: deviceInfo.operatingSystem,
        userAgent: deviceInfo.userAgent,
        referrer_url: document.referrer || undefined,
        landing_page_url: window.location.href,
        utm_source: params.get('utm_source') || undefined,
        utm_medium: params.get('utm_medium') || undefined,
        utm_campaign: params.get('utm_campaign') || undefined,
        utm_term: params.get('utm_term') || undefined,
        utm_content: params.get('utm_content') || undefined
      };

      // Send data to Edge Function
      const { data: result, error } = await sentryUtils.trackSupabaseFunction(
        'capture-subscription',
        () => supabase.functions.invoke('capture-subscription', { body: subscriptionData }),
        { email: userEmail, interests: data.interests }
      );

      if (error) {
        sentryUtils.captureError(new Error('Subscription capture failed'), {
          email: userEmail,
          error: error.message,
          subscriptionData
        });
        
        // Handle duplicate email case
        if (error.message?.includes('EMAIL_EXISTS') || result?.error === 'EMAIL_EXISTS') {
          toast({
            title: t('newsletterForm.toasts.emailExists.title'),
            description: t('newsletterForm.toasts.emailExists.description'),
            variant: "default",
          });
          return;
        }
        
        toast({
          title: t('newsletterForm.toasts.processingError.title'),
          description: t('newsletterForm.toasts.processingError.description'),
          variant: "destructive",
        });
        return;
      }

      // Track subscription in analytics platforms with GTM
      trackGTMEvent('prospect_completed', { user_email: userEmail.trim() });
      trackEventMp('prospect_completed', { user_email: userEmail.trim() });
      trackGTMConversionFunnel('signup_completed', 5, 10);
      trackConversionFunnelHj('signup_completed', 5);
      trackConversionFunnelMp('signup_completed', 5);

      // Legacy flow: close modal and show success
      console.log('[NEWSLETTER-FORM] Legacy flow: completing subscription');
      setShowModal(false);
      emailForm.reset();
      interestsForm.reset();
      
      onSuccess?.(userEmail.trim());
    } catch (error) {
      sentryUtils.captureError(error instanceof Error ? error : new Error(String(error)), {
        context: 'final_submit',
        email: userEmail,
        interests: data.interests
      });
      toast({
        title: t('newsletterForm.toasts.unexpectedError.title'),
        description: t('newsletterForm.toasts.unexpectedError.description'),
        variant: "destructive",
      });
    }
  };

  const handlePlanSelected = (plan: 'monthly' | 'yearly') => {
    console.log('[NEWSLETTER-FORM] Plan selected:', plan);
    setSelectedPlan(plan);
    setShowPlanSelector(false);
    setShowInlineCheckout(true);
    
    trackGTMEvent('inline_checkout_plan_selected', { email: userEmail, plan });
    trackEventMp('inline_checkout_plan_selected', { email: userEmail, plan });
  };

  return (
    <ErrorBoundary>
      {/* Step 1: Email Form */}
      <Form {...emailForm}>
        <form onSubmit={emailForm.handleSubmit(handleEmailSubmit)} className="w-full max-w-2xl mx-auto space-y-6 animate-fade-in-up">
          <FormField
            control={emailForm.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem className="space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 text-newsletter-primary">
                    <Mail className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('newsletterForm.emailLabel')}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Shield className="w-3 h-3 text-green-600" />
                    <span>{t('newsletterForm.secureText')}</span>
                  </div>
                </div>
                <FormControl>
                  <Input
                    id="newsletter-email-input"
                    type="email"
                    placeholder={t('newsletterForm.emailPlaceholder')}
                    {...field}
                    className="
                      h-12 sm:h-14 px-4 sm:px-6 text-sm sm:text-base rounded-xl sm:rounded-2xl
                      bg-background/5 backdrop-blur-sm 
                      border-2 border-newsletter-primary/20 
                      focus:border-newsletter-primary/50
                      placeholder:text-muted-foreground/60
                      transition-all duration-300 ease-out
                      hover:border-newsletter-primary/30
                      focus:shadow-newsletter
                      data-[invalid]:border-destructive/50
                      data-[invalid]:focus:border-destructive
                    "
                    data-invalid={fieldState.error ? true : undefined}
                    onFocus={() => {
                      trackGTMFormEngagement('newsletter_form', 'email_focused', 'email');
                      trackFormEngagementHj('newsletter_form', 'email_focused');
                      trackFormEngagementMp('newsletter_form', 'email_focused');
                    }}
                    onBlur={() => {
                      trackGTMFormEngagement('newsletter_form', 'email_blurred', 'email');
                      trackFormEngagementHj('newsletter_form', 'email_blurred');
                      trackFormEngagementMp('newsletter_form', 'email_blurred');
                    }}
                  />
                </FormControl>
                <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
                  <Mail className="w-4 h-4 mt-0.5 text-newsletter-primary flex-shrink-0" />
                  <div>
                    <p className="mb-1"><strong>{t('newsletterForm.freeNewsletterText')}</strong></p>
                    <p>{t('newsletterForm.guarantees')}</p>
                  </div>
                </div>
                <FormMessage className="text-destructive text-sm" />
              </FormItem>
            )}
          />

          <Button
            id="newsletter-form-button"
            type="submit"
            disabled={emailForm.formState.isSubmitting}
            variant="premium"
            className="
              w-full h-12 sm:h-14 text-sm sm:text-base font-semibold rounded-xl sm:rounded-2xl
              transition-all ease-out
              disabled:opacity-50 disabled:scale-100
              animate-smooth-pulse hover:animate-none
              shadow-newsletter
              group
            "
          >
            {emailForm.formState.isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                {t('newsletterForm.validating')}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                {t('newsletterForm.submitButton')}
                <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            )}
          </Button>
        </form>
      </Form>

      {/* Step 2: Interests Modal */}
      <Dialog open={showModal} onOpenChange={(open) => {
        if (!open) {
          // Track modal close with GTM
          trackGTMEvent('newsletter_modal_closed', {
            event_category: 'form_interaction',
            event_label: 'modal_dismissed'
          });
          trackEventMp('newsletter_modal_closed', {
            event_category: 'form_interaction',
            event_label: 'modal_dismissed'
          });
          trackFormEngagementHj('newsletter_form', 'modal_closed');
          trackFormEngagementMp('newsletter_form', 'modal_closed');
          trackGTMConversionFunnel('modal_abandoned', 3);
          trackConversionFunnelHj('modal_abandoned', 3);
          trackConversionFunnelMp('modal_abandoned', 3);
        }
        setShowModal(open);
      }}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 bg-gradient-to-r from-newsletter-primary/5 to-newsletter-secondary/5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 rounded-full bg-newsletter-primary/20 flex items-center justify-center">
                <CheckCircle className="w-4 h-4 text-newsletter-primary" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-xl font-bold text-newsletter-primary">
                  {t('newsletterForm.finalStep.title')}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('newsletterForm.finalStep.subtitle')}
                </p>
              </div>
            </div>
            
            {/* Progress indicator */}
            <div className="flex items-center gap-2 mt-4">
              <div className="flex-1 h-2 bg-newsletter-primary rounded-full" />
              <div className="flex-1 h-2 bg-newsletter-primary rounded-full" />
              <span className="text-xs text-newsletter-primary font-medium ml-2">2/2</span>
            </div>
          </DialogHeader>
          
          <div className="p-6 pt-2">
            {/* Show email confirmation */}
            <EditableEmail 
              email={userEmail}
              onEmailChange={(newEmail) => {
                setUserEmail(newEmail);
                emailForm.setValue('email', newEmail);
              }}
              source="newsletter_form"
            />

            <Form {...interestsForm}>
              <form onSubmit={interestsForm.handleSubmit(handleFinalSubmit)} className="space-y-4">
                <FormField
                  control={interestsForm.control}
                  name="interests"
                  render={({ field, fieldState }) => (
                    <FormItem className="space-y-4">
                      <div className="flex items-center gap-2 text-newsletter-primary">
                        <Sparkles className="w-5 h-5" />
                        <span className="text-sm font-medium">{t('newsletterForm.finalStep.interestsTitle')}</span>
                      </div>
                      <FormControl>
                        <NewsletterTextarea 
                          value={field.value} 
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <Button
                  id="newsletter-modal-finalize-button"
                  type="submit"
                  disabled={interestsForm.formState.isSubmitting}
                  variant="premium"
                  className="
                    w-full h-12 text-base font-semibold rounded-xl
                    transition-all ease-out
                    disabled:opacity-50
                    shadow-newsletter
                    group
                  "
                >
                  {interestsForm.formState.isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                      {t('newsletterForm.finalStep.finalizing')}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                      {t('newsletterForm.finalStep.finalizeButton')}
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </div>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Plan Selector Modal - Inline Checkout Flow */}
      {inlineCheckoutEnabled && pricingData && (
        <>
          <PlanSelector 
            open={showPlanSelector}
            onOpenChange={setShowPlanSelector}
            pricingData={pricingData}
            onPlanSelected={handlePlanSelected}
          />

          <InlineCheckoutModal
            open={showInlineCheckout}
            onOpenChange={setShowInlineCheckout}
            userEmail={userEmail}
            selectedPlan={selectedPlan}
            pricingData={pricingData}
          />
        </>
      )}
    </ErrorBoundary>
  );
};